/**
 * subscription-quantity-gate.js
 *
 * Controls visibility AND state of the Shopify Subscriptions app block
 * based on the current quantity input value on the product page.
 *
 * Rules:
 *  - Quantity < 10  → Hide subscription; FULLY reset to one-time purchase;
 *                     clear selling_plan hidden input so cart never receives it.
 *  - Quantity >= 10 → Show subscription; customer can choose either option.
 *
 * HOW QUANTITY CHANGES ARE DETECTED (all paths covered):
 *
 *  Path A — quantity-input custom element (buy-buttons.liquid)
 *    onButtonClick() dispatches `new Event('change', {bubbles:true})` on the input
 *    → our `change` listener fires ✓
 *
 *  Path B — quantity-selector custom element (quantity-input.liquid)
 *    onButtonClick() mutates `this.input.quantity` (non-standard property), NOT
 *    `this.input.value`, so the conditional `previousValue !== this.input.value`
 *    is always false → the `change` event is NEVER dispatched.
 *    → our document-level click listener on `.quantity__button` fires instead ✓
 *
 *  Path C — manual keyboard input in the text field
 *    → our `input` listener fires ✓
 *
 *  Path D — programmatic reset after Add to Cart (setQuantityBoundries in theme.js)
 *    Sets input.value = min without a DOM event, then publishes `quantity-update`
 *    via FoxTheme.pubsub → our pubsub subscriber fires ✓
 *    Also: `product-ajax:added` document event as secondary hook ✓
 */

(function () {
  'use strict';

  const SUBSCRIPTION_THRESHOLD = 1;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function getProductContainer(quantityInput) {
    // We strictly limit the scope to product forms on the page.
    // We intentionally DO NOT fallback to document.body, otherwise quantity
    // inputs in the Cart Drawer with value >= 10 would accidentally trigger
    // the product page subscription block.
    return (
      quantityInput.closest('product-info') ||
      quantityInput.closest('.product__info-container') ||
      quantityInput.closest('.product-form-wrapper') ||
      null
    );
  }

  function getSubscriptionBlock(container) {
    if (!container) return null;
    return container.querySelector(
      '.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)'
    );
  }

  function getSubscriptionWrapper(block) {
    if (!block) return null;
    return (
      block.closest('.shopify-app-block') ||
      block.closest('[class*="product__block--"]') ||
      block.parentElement
    );
  }

  // ─── Core Logic ───────────────────────────────────────────────────────────

  function hideSubscription(container) {
    if (!container) return;
    const block = getSubscriptionBlock(container);
    if (!block) return;

    // 1. Switch radio to one-time purchase unconditionally
    const oneTimeRadio = block.querySelector('input[data-radio-type="one_time_purchase"]');
    const subscriptionRadio = block.querySelector('input[data-radio-type="selling_plan_group"]');

    if (oneTimeRadio && !oneTimeRadio.checked) {
      oneTimeRadio.checked = true;
      oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (subscriptionRadio && subscriptionRadio.checked) {
      subscriptionRadio.checked = false;
      subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // 2. Clear ALL selling_plan hidden inputs so cart payload never gets a stale plan
    document.querySelectorAll(
      'input.selected-selling-plan-id, input[name="selling_plan"]'
    ).forEach(function (input) {
      if (input.value !== '') {
        input.value = '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // 3. Stamp data attribute — theme.js guard reads this to skip subscription state
    block.setAttribute('data-subscription-gate-hidden', 'true');

    // 4. Hide the wrapper
    const wrapper = getSubscriptionWrapper(block);
    if (wrapper) {
      wrapper.style.display = 'none';
    } else {
      block.style.display = 'none';
    }
  }

  function showSubscription(container) {
    if (!container) return;
    const block = getSubscriptionBlock(container);
    if (!block) return;

    block.removeAttribute('data-subscription-gate-hidden');

    const wrapper = getSubscriptionWrapper(block);
    if (wrapper) {
      wrapper.style.display = '';
    } else {
      block.style.display = '';
    }
  }

  function evaluateQuantity(quantityInput) {
    // Only process quantity inputs that belong to a product form
    const container = getProductContainer(quantityInput);
    if (!container) return;

    const qty = parseInt(quantityInput.value, 10);

    if (isNaN(qty) || qty < SUBSCRIPTION_THRESHOLD) {
      hideSubscription(container);
    } else {
      showSubscription(container);
    }
  }

  /** Re-evaluate every quantity input currently on the page. */
  function reEvaluateAll() {
    document.querySelectorAll('.quantity__input').forEach(evaluateQuantity);
  }

  // ─── Listener Setup ───────────────────────────────────────────────────────

  /**
   * Path A & C: native DOM events fired by quantity-input for button clicks,
   * and by any element for keyboard input.
   */
  function attachListeners(quantityInput) {
    // Ignore quantity inputs that are not part of a product container (like cart items)
    if (!getProductContainer(quantityInput)) return;

    if (quantityInput.dataset.subscriptionGateAttached) return;
    quantityInput.dataset.subscriptionGateAttached = 'true';

    const handler = () => evaluateQuantity(quantityInput);
    quantityInput.addEventListener('change', handler);
    quantityInput.addEventListener('input', handler);

    // Evaluate immediately on attachment
    handler();
  }

  function init() {
    document.querySelectorAll('.quantity__input').forEach(attachListeners);
  }

  /**
   * Path B: quantity-selector does NOT dispatch a `change` event from its button
   * clicks (it mutates input.quantity instead of input.value). Catch button clicks
   * at the document level and re-evaluate after the custom element handler runs.
   */
  function hookQuantityButtonClicks() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.quantity__button');
      if (!btn) return;

      // Let the custom element's own click handler run first, then re-evaluate.
      // Two ticks: one for the synchronous handler, one for any microtasks.
      setTimeout(reEvaluateAll, 0);
    }, true /* capture phase — before the element's own handler */);
  }

  /**
   * Path D-1: FoxTheme pubsub `quantity-update` — published by setQuantityBoundries()
   * in theme.js immediately after it programmatically sets input.value = min.
   * This fires after every successful Add to Cart that resets the quantity counter.
   */
  function hookPubSub() {
    function trySubscribe() {
      if (window.FoxTheme && window.FoxTheme.pubsub) {
        FoxTheme.pubsub.subscribe('quantity-update', function () {
          setTimeout(reEvaluateAll, 0);
        });
        FoxTheme.pubsub.subscribe('quantity-boundries', function () {
          setTimeout(reEvaluateAll, 0);
        });
      } else {
        setTimeout(trySubscribe, 100);
      }
    }
    trySubscribe();
  }

  /**
   * Path D-2: document-level events as secondary hooks for programmatic resets.
   */
  function hookDocumentEvents() {
    document.addEventListener('product-ajax:added', function () {
      setTimeout(reEvaluateAll, 50);
    });
    document.addEventListener('cart:refresh', function () {
      setTimeout(reEvaluateAll, 50);
    });
  }

  /**
   * MutationObserver: attach listeners to quantity inputs added dynamically
   * (quick-view, variant re-renders, sticky ATC bar, etc.).
   */
  function observeDOMChanges() {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (node.matches && node.matches('.quantity__input')) {
            attachListeners(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('.quantity__input').forEach(attachListeners);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function syncSellingPlanToMainForm() {
    // The Shopify subscription app block sometimes attaches its hidden input to the wrong form (e.g. installment form).
    // This ensures the main add-to-cart form always receives the correct selling_plan value.
    function updateMainForm() {
      const visibleBlock = document.querySelector('.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)');
      let val = '';
      if (visibleBlock) {
        const selectedRadio = visibleBlock.querySelector('input[name^="purchaseOption_"]:checked');
        if (selectedRadio && selectedRadio.getAttribute('data-radio-type') === 'selling_plan') {
          val = selectedRadio.getAttribute('data-selling-plan-id') || '';
        }
      }
      
      document.querySelectorAll('form[data-type="add-to-cart-form"]').forEach(form => {
        let hiddenInput = form.querySelector('input[name="selling_plan"]');
        if (!hiddenInput) {
          hiddenInput = document.createElement('input');
          hiddenInput.type = 'hidden';
          hiddenInput.name = 'selling_plan';
          form.appendChild(hiddenInput);
        }
        hiddenInput.value = val;
        // If it's empty, we should ideally disable or remove the input so it doesn't submit an empty selling_plan
        if (!val) {
          hiddenInput.disabled = true;
        } else {
          hiddenInput.disabled = false;
        }
      });
    }

    document.addEventListener('change', function (e) {
      const target = e.target;
      if (target.matches('input[data-radio-type="selling_plan"], input[data-radio-type="one_time_purchase"]')) {
        updateMainForm();
      }
    });

    document.addEventListener('submit', function (e) {
      const form = e.target;
      if (form.matches('form[data-type="add-to-cart-form"]') || form.classList.contains('main-product-form')) {
        updateMainForm();
      }
    }, true); // capture phase
  }

  // ─── Entry Point ──────────────────────────────────────────────────────────

  function boot() {
    init();
    hookQuantityButtonClicks(); // Path B — CRITICAL for quantity-selector
    hookPubSub();               // Path D-1 — post cart add reset
    hookDocumentEvents();       // Path D-2 — secondary post cart add hooks
    observeDOMChanges();        // Dynamic input detection
    syncSellingPlanToMainForm(); // Ensure selling plan gets to the main form
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
